import { useEffect, useMemo, useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { useNavigate } from "react-router";
import {
  BarChart3,
  Send,
  Save,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Calendar,
  ArrowRight,
  CreditCard,
  Bot,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { PageHeader } from "../layout/PageHeader";
import { getSupabaseClient } from "../providers/supabase/supabase";

type IntegrationId = "posthog" | "billionmail" | "stripe";

type FieldDef = {
  key: string;
  label: string;
  placeholder?: string;
  secret?: boolean;
  helpText?: string;
};

type IntegrationDef = {
  id: IntegrationId;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  fields: FieldDef[];
  setupSteps: { title: string; body: string | React.ReactNode }[];
};

const INTEGRATIONS: IntegrationDef[] = [
  {
    id: "posthog",
    title: "PostHog",
    subtitle: "Affiche l'activité produit d'un contact dans sa fiche",
    icon: BarChart3,
    fields: [
      {
        key: "host",
        label: "Host",
        placeholder: "https://eu.i.posthog.com",
      },
      {
        key: "project_id",
        label: "Project ID",
        placeholder: "12345",
      },
      {
        key: "personal_api_key",
        label: "Personal API Key",
        secret: true,
        placeholder: "phx_...",
        helpText:
          "Créer une clé personnelle avec scopes read events + persons dans PostHog → Settings",
      },
    ],
    setupSteps: [
      {
        title: "1. Récupérer l'URL (Host)",
        body: "EU → https://eu.i.posthog.com — US → https://us.i.posthog.com — self-hosted → l'URL de ton instance.",
      },
      {
        title: "2. Récupérer le Project ID",
        body: "PostHog → Settings du projet → l'ID numérique est visible en haut (ex: 12345).",
      },
      {
        title: "3. Créer une Personal API Key",
        body: "PostHog → Top-right avatar → Personal API keys → Create personal API key. Scopes minimum : Read sur Events + Persons + Session recordings (optionnel). Copier la clé (commence par phx_).",
      },
      {
        title: "4. Coller dans le CRM",
        body: "Coller les 3 valeurs ci-dessous, activer le switch, enregistrer. Les données apparaîtront dans la fiche de chaque contact (section « Activité produit »), matchées sur l'email.",
      },
    ],
  },
  {
    id: "billionmail",
    title: "BillionMail",
    subtitle:
      "Pousse les contacts dans la liste de prospection + envoi d'emails transactionnels",
    icon: Send,
    fields: [
      {
        key: "base_url",
        label: "URL BillionMail (sans slash final)",
        placeholder: "https://mail.fabrik.so",
      },
      {
        key: "admin_key",
        label: "Admin API Key (Bearer, pour gérer les contacts/listes)",
        secret: true,
        helpText:
          "Le JWT bm_options.API_TOKEN, à lire sur le VPS (voir docs/BRANCHER_BILLIONMAIL.md §2). Il est aussi codé en dur dans le vhost nginx : le faire tourner casse la sync tant que le vhost n'est pas retouché.",
      },
      {
        key: "send_key",
        label: "Send API Key (X-API-Key, pour envoyer des mails)",
        secret: true,
        helpText:
          "Inutile pour la sync des contacts, aucun code ne la lit. Elle servira à l'envoi (Sending API), qui n'existe pas encore.",
      },
      {
        key: "list_id",
        label: "ID de la liste de prospection",
        placeholder: "1",
        helpText:
          "1 = fabrik-prospection-2026 (cold outbound). Ne jamais y pointer une liste d'abonnés newsletter : les deux bases légales ne se mélangent pas.",
      },
      {
        key: "contacts_endpoint",
        label: "Endpoint d'ajout de contact",
        placeholder: "/api/contact/group/import",
        helpText:
          "/api/contact/group/import sur BillionMail 4.9.3. Il n'y a pas de Swagger sur cette instance : les routes se lisent dans /opt/BillionMail/core/api/**/*.go.",
      },
      {
        key: "default_sender",
        label: "Expéditeur par défaut (optionnel)",
        placeholder: "noreply@fabrik.so",
      },
    ],
    setupSteps: [
      {
        title: "1. La GUI BillionMail est fermée",
        body: "nginx n'expose publiquement que /api/contact/, et uniquement avec le JWT admin exact — l'admin et le reste de l'API répondent 403. Il n'y a donc rien à cliquer dans BillionMail : tout se relève en SSH sur 72.61.98.252. Le détail est dans docs/BRANCHER_BILLIONMAIL.md.",
      },
      {
        title: "2. Les quatre valeurs",
        body: "base_url = https://mail.fabrik.so · admin_key = le JWT bm_options.API_TOKEN (psql dans le conteneur billionmail-pgsql) · list_id = 1 · contacts_endpoint = /api/contact/group/import.",
      },
      {
        title: "3. Tester",
        body: "Remplir, activer Actif, enregistrer, puis « Tester avec un contact ». Attention : BillionMail répond HTTP 200 même sur erreur — c'est le champ ok de la réponse qui fait foi, pas le code HTTP.",
      },
      {
        title: "4. Avant d'envoyer quoi que ce soit",
        body: "Aucun gabarit d'email n'existe encore côté BillionMail. La liste est du cold outbound en single opt-in : lien de désinscription et identification de l'émetteur dans le gabarit sont des conditions de licéité, pas des options.",
      },
    ],
  },
  {
    id: "stripe",
    title: "Stripe",
    subtitle:
      "Trésorerie en temps réel + revenus / abonnements dans la page Affaires",
    icon: CreditCard,
    fields: [
      {
        key: "secret_key",
        label: "Secret Key",
        secret: true,
        placeholder: "sk_live_... ou sk_test_...",
        helpText:
          "Stripe Dashboard → Developers → API keys → Secret key. Restricted key OK si scopes : balance read, payouts read, customers read, invoices read, charges read, subscriptions read.",
      },
      {
        key: "webhook_secret",
        label: "Webhook Signing Secret",
        secret: true,
        placeholder: "whsec_...",
        helpText:
          "Affiché une seule fois à la création du webhook (étape 2 ci-dessous). Si perdu, recréer un webhook.",
      },
    ],
    setupSteps: [
      {
        title: "1. Récupérer la Secret Key",
        body: "Stripe Dashboard → Developers → API keys → copier la Secret key (commence par sk_live_ en prod, sk_test_ en test). Idéalement créer une Restricted Key avec scopes en lecture seule + webhook signing : Balance, Charges, Customers, Invoices, Payouts, Subscriptions, Webhook Endpoints.",
      },
      {
        title: "2. Créer le webhook",
        body: "Stripe Dashboard → Developers → Webhooks → Add endpoint → coller l'URL : https://luibovhuvqnznucfwvym.functions.supabase.co/stripe-webhook → événements à écouter : invoice.paid, invoice.payment_failed, charge.succeeded, charge.refunded, charge.failed, payment_intent.succeeded, payment_intent.payment_failed, customer.subscription.created, customer.subscription.updated, customer.subscription.deleted, payout.created, payout.paid, payout.failed, payout.canceled. Une fois créé, cliquer « Reveal » à côté de Signing secret et copier la valeur (whsec_...).",
      },
      {
        title: "3. Coller dans le CRM",
        body: "Coller Secret Key + Webhook Signing Secret ci-dessous, activer le switch Actif, enregistrer.",
      },
      {
        title: "4. Tester la connexion",
        body: "Une fois enregistré et activé, le bouton « Tester la connexion » apparaît sous la carte. Il appelle Stripe pour récupérer la balance — si ça affiche un solde, c'est branché. La trésorerie apparaîtra ensuite dans la page Affaires + un widget sur le dashboard.",
      },
      {
        title: "5. Lier des companies à des Stripe customers (optionnel)",
        body: "Sur la fiche d'une company CRM, coller son ID Stripe (cus_xxx) dans le champ « Stripe customer ID ». Tous les paiements et abonnements de ce customer seront alors visibles dans la fiche. Le webhook fait aussi un fallback automatique par email du contact lié.",
      },
    ],
  },
];

type IntegrationRow = {
  id: IntegrationId;
  config: Record<string, string | null | undefined>;
  enabled: boolean;
  updated_at: string;
};

export const IntegrationsPage = () => {
  const { identity } = useGetIdentity();
  const navigate = useNavigate();
  const isAdmin = !!(identity as unknown as { administrator?: boolean })
    ?.administrator;

  const [rows, setRows] = useState<Record<
    IntegrationId,
    IntegrationRow
  > | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const { data, error } = await getSupabaseClient()
        .from("crm_integrations")
        .select("*");
      if (error) {
        console.error(error);
        setLoading(false);
        return;
      }
      const map: Record<string, IntegrationRow> = {};
      (data ?? []).forEach((r: IntegrationRow) => (map[r.id] = r));
      setRows(map as Record<IntegrationId, IntegrationRow>);
      setLoading(false);
    })();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Card className="p-6 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <div>
            <p className="font-medium">Accès restreint</p>
            <p className="text-sm text-muted-foreground">
              Seuls les administrateurs peuvent configurer les intégrations.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="ml-auto"
          >
            Retour
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Intégrations"
        subtitle="Connecte les services externes utilisés par le CRM"
      />

      {loading ? (
        <p className="text-sm text-muted-foreground p-4">Chargement…</p>
      ) : (
        <div className="flex flex-col gap-4 max-w-2xl">
          <PointerCardWithTutorial
            icon={Calendar}
            title="Google Calendar"
            subtitle="Connexion par utilisateur (synchronisation des RDV avec ton agenda)"
            ctaLabel="Ouvrir mon profil"
            onCta={() => navigate("/profile")}
            steps={[
              {
                title: "1. Prérequis admin (à faire une seule fois)",
                body: "Supabase Dashboard → Authentication → Providers → Google : activer + coller le Client ID + Client Secret d'un projet Google Cloud OAuth. Scopes Google Cloud à autoriser : email, profile, openid, https://www.googleapis.com/auth/calendar. Ajouter https://luibovhuvqnznucfwvym.supabase.co/auth/v1/callback comme redirect URI dans Google Cloud Console.",
              },
              {
                title: "2. Connecter son compte (chaque sales)",
                body: "Avatar en bas de la sidebar → Profil → carte Google Calendar → bouton « Connecter mon Google Calendar ». Approuver les permissions calendrier. Le token est stocké côté Supabase et accessible aux edge functions du CRM.",
              },
              {
                title: "3. Vérification",
                body: "Dans le profil, le badge passe à « Lié à mon@email.com ». Côté DB, une row apparaît dans user_oauth_tokens pour le provider 'google'. Si vide → la connexion n'a pas abouti.",
              },
              {
                title: "4. Quand ça marche",
                body: "Les RDV créés dans le CRM ont un google_event_id et apparaissent dans Google Calendar. La carte d'agenda dans /my-day pull les events des prochains 7 jours du calendrier perso.",
              },
            ]}
          />

          <PointerCardWithTutorial
            icon={Bot}
            title="Fournisseurs IA (Anthropic / DeepSeek / OpenRouter)"
            subtitle="Configuration par utilisateur — détermine quel modèle anime l'agent du CRM"
            ctaLabel="Configurer"
            onCta={() => navigate("/settings/ai-providers")}
            steps={[
              {
                title:
                  "Option A — DeepSeek (recommandé pour démarrer, le moins cher)",
                body: "1) Aller sur platform.deepseek.com → s'inscrire. 2) API Keys → Create new API key (commence par sk-). 3) Recharger 5-10 € de crédit (deepseek-chat = 0.27 $ / 1M tokens en input). 4) Dans le CRM → /settings/ai-providers → onglet DeepSeek → coller la clé → Modèle = deepseek-chat → Enregistrer. ⚠️ NE PAS choisir deepseek-reasoner (incompat tools).",
              },
              {
                title:
                  "Option B — Anthropic via OAuth Claude.ai (sans clé API)",
                body: "1) /settings/ai-providers → onglet Anthropic → bouton « Connecter mon compte Claude.ai ». 2) Approuver l'OAuth. 3) Le token apparaît dans user_oauth_tokens (provider = anthropic). Avantage : pas de clé à gérer, quotas suivent ton compte Claude.ai. Inconvénient : limites de taux plus serrées qu'une clé API directe.",
              },
              {
                title: "Option C — Anthropic via API key (production sérieuse)",
                body: "1) console.anthropic.com → API Keys → Create Key. 2) /settings/ai-providers → onglet Anthropic → coller la clé. 3) Modèle = claude-sonnet-4-6 ou claude-opus-4-7 selon budget.",
              },
              {
                title: "Option D — OpenRouter (pour tester d'autres modèles)",
                body: "openrouter.ai → API Keys → Create. Coller dans /settings/ai-providers. Permet GPT-4o, Llama, Gemini etc via une seule facture.",
              },
              {
                title: "Choix du provider actif",
                body: "Le chat de l'agent utilise dans cet ordre : 1) clé Anthropic si présente, 2) OAuth Anthropic, 3) DeepSeek, 4) OpenRouter. Pour forcer un provider précis, configurer uniquement celui-là.",
              },
            ]}
          />

          {INTEGRATIONS.map((def) => (
            <IntegrationCard
              key={def.id}
              def={def}
              row={rows?.[def.id]}
              onSaved={(next) =>
                setRows((prev) =>
                  prev
                    ? { ...prev, [def.id]: next }
                    : ({ [def.id]: next } as Record<
                        IntegrationId,
                        IntegrationRow
                      >),
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

IntegrationsPage.path = "/settings/integrations";

function PointerCardWithTutorial({
  icon: Icon,
  title,
  subtitle,
  ctaLabel,
  onCta,
  steps,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  ctaLabel: string;
  onCta: () => void;
  steps: { title: string; body: string | React.ReactNode }[];
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  return (
    <Card className="border-dashed">
      <CardContent className="pt-5 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-muted">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold">{title}</p>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onCta}
            className="shrink-0"
          >
            {ctaLabel} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        <div className="rounded-md border border-dashed border-border bg-muted/30">
          <button
            type="button"
            onClick={() => setHelpOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium"
          >
            <span className="flex items-center gap-1.5">
              <HelpCircle className="h-3.5 w-3.5" />
              Tutoriel pas à pas
            </span>
            {helpOpen ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
          {helpOpen && (
            <ol className="px-4 pb-3 pt-1 flex flex-col gap-2">
              {steps.map((step, idx) => (
                <li key={idx} className="text-xs">
                  <p className="font-medium">{step.title}</p>
                  <p className="text-muted-foreground whitespace-pre-line">
                    {step.body}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function IntegrationCard({
  def,
  row,
  onSaved,
}: {
  def: IntegrationDef;
  row: IntegrationRow | undefined;
  onSaved: (next: IntegrationRow) => void;
}) {
  const notify = useNotify();
  const Icon = def.icon;
  const [enabled, setEnabled] = useState(row?.enabled ?? false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(() =>
    emptyValues(def, row),
  );
  const [dirtySecrets, setDirtySecrets] = useState<Record<string, boolean>>({});

  const configured = useMemo(() => isConfigured(def, row), [def, row]);

  // Auto-open the tutorial section when the integration is not yet configured.
  // Once configured, leave it collapsed (the user can re-open if needed).
  const [helpOpen, setHelpOpen] = useState(!configured);
  useEffect(() => {
    if (!configured) setHelpOpen(true);
  }, [configured]);

  const filledFieldCount = def.fields.filter((f) => {
    const v = row?.config?.[f.key];
    return typeof v === "string" && v.trim().length > 0;
  }).length;
  const totalFieldCount = def.fields.length;

  const handleChange = (key: string, value: string, isSecret: boolean) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (isSecret) setDirtySecrets((prev) => ({ ...prev, [key]: true }));
  };

  const handleSave = async () => {
    setSaving(true);

    // Preserve secret values not touched by the user by re-reading from DB.
    // The masked UI never shows the real secret, so if the input is still the
    // placeholder mask we must not overwrite the DB field with it.
    const nextConfig: Record<string, string> = {};
    for (const field of def.fields) {
      const value = values[field.key] ?? "";
      if (field.secret && !dirtySecrets[field.key]) {
        // Keep existing value in DB (do not include in patch)
        continue;
      }
      nextConfig[field.key] = value;
    }

    // Merge into existing config so we don't drop untouched non-secret fields
    const merged = { ...(row?.config ?? {}), ...nextConfig };

    const { data, error } = await getSupabaseClient()
      .from("crm_integrations")
      .update({ config: merged, enabled })
      .eq("id", def.id)
      .select("*")
      .single();

    if (error) {
      notify(`Erreur: ${error.message}`, { type: "error" });
      setSaving(false);
      return;
    }
    notify("Intégration enregistrée", { type: "success" });
    onSaved(data as IntegrationRow);
    setDirtySecrets({});
    setValues(emptyValues(def, data as IntegrationRow));
    setSaving(false);
  };

  return (
    <Card>
      <CardContent className="p-5 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-muted">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold">{def.title}</h3>
              {configured ? (
                <Badge variant="secondary" className="gap-1">
                  <Check className="h-3 w-3" /> Configuré
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1">
                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                  {filledFieldCount}/{totalFieldCount} champs renseignés
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{def.subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor={`enabled-${def.id}`} className="text-xs">
              Actif
            </Label>
            <Switch
              id={`enabled-${def.id}`}
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>
        </div>

        <div className="rounded-md border border-dashed border-border bg-muted/30">
          <button
            type="button"
            onClick={() => setHelpOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium"
          >
            <span className="flex items-center gap-1.5">
              <HelpCircle className="h-3.5 w-3.5" />
              Tutoriel pas à pas
            </span>
            {helpOpen ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
          {helpOpen && (
            <ol className="px-4 pb-3 pt-1 flex flex-col gap-2">
              {def.setupSteps.map((step, idx) => (
                <li key={idx} className="text-xs">
                  <p className="font-medium">{step.title}</p>
                  <p className="text-muted-foreground">{step.body}</p>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {def.fields.map((field) => {
            const hasStoredSecret = field.secret && !!row?.config?.[field.key];
            const currentValue = values[field.key] ?? "";
            return (
              <div key={field.key} className="flex flex-col gap-1.5">
                <Label
                  htmlFor={`${def.id}-${field.key}`}
                  className="text-xs font-medium"
                >
                  {field.label}
                </Label>
                <Input
                  id={`${def.id}-${field.key}`}
                  type={field.secret ? "password" : "text"}
                  value={currentValue}
                  placeholder={
                    hasStoredSecret && !dirtySecrets[field.key]
                      ? "••••••••  (déjà configuré, saisir pour remplacer)"
                      : field.placeholder
                  }
                  onChange={(e) =>
                    handleChange(field.key, e.target.value, !!field.secret)
                  }
                />
                {field.helpText && (
                  <p className="text-[11px] text-muted-foreground">
                    {field.helpText}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button onClick={handleSave} disabled={saving} size="sm">
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>

        {def.id === "billionmail" && configured && enabled && (
          <BillionMailTestBlock />
        )}
        {def.id === "stripe" && configured && enabled && (
          <>
            <StripeTestBlock />
            <StripeBackfillBlock />
          </>
        )}
      </CardContent>
    </Card>
  );
}

const stripeSummary = (resp: Record<string, unknown>) => {
  const balance = resp.balance as
    | {
        available?: Record<string, number>;
        pending?: Record<string, number>;
      }
    | undefined;
  if (!resp.ok || !balance) return resp.message as string | undefined;
  const fmt = (cents: number, ccy: string) =>
    `${(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${ccy.toUpperCase()}`;
  const list = (m: Record<string, number> = {}) =>
    Object.entries(m)
      .map(([ccy, c]) => fmt(c, ccy))
      .join(", ") || "0";
  return `Disponible: ${list(balance.available)} • En attente: ${list(balance.pending)}`;
};

function StripeTestBlock() {
  return (
    <EdgeFnTestBlock
      title="Tester la connexion"
      description="Appelle Stripe pour récupérer la balance live. Confirme que la secret key marche et donne un aperçu de la trésorerie actuelle."
      fn="get_stripe_treasury"
      body={() => ({})}
      runningLabel="Appel Stripe…"
      summarize={stripeSummary}
    />
  );
}

function StripeBackfillBlock() {
  const notify = useNotify();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<string[]>([]);
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sinceDays, setSinceDays] = useState<number>(365);

  const run = async () => {
    if (
      !confirm(
        `Lancer un backfill Stripe sur les ${sinceDays} derniers jours ?\nDurée typique : 1-3 minutes.`,
      )
    )
      return;
    setRunning(true);
    setProgress([]);
    setCounts(null);
    setError(null);

    try {
      const supabase = getSupabaseClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("not_authenticated");

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-backfill`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ since_days: sinceDays }),
      });
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() ?? "";
        for (const block of blocks) {
          const eventLine = /^event:\s*(.+)$/m.exec(block)?.[1]?.trim();
          const dataLine = /^data:\s*(.+)$/m.exec(block)?.[1]?.trim();
          if (!eventLine || !dataLine) continue;
          let data: Record<string, unknown> = {};
          try {
            data = JSON.parse(dataLine);
          } catch {
            continue;
          }
          if (eventLine === "backfill.progress") {
            const note = String(data.note ?? "");
            setProgress((p) => [...p, note]);
            if (data.counts) setCounts(data.counts as Record<string, number>);
          } else if (eventLine === "backfill.done") {
            setCounts((data.counts ?? {}) as Record<string, number>);
          } else if (eventLine === "backfill.error") {
            setError(String(data.error ?? "unknown"));
          }
        }
      }
      notify("Backfill Stripe terminé", { type: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      notify(`Backfill échoué : ${msg}`, { type: "error" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-muted/20 p-3 flex flex-col gap-2">
      <p className="text-xs font-medium">Backfill Stripe → CRM</p>
      <p className="text-[11px] text-muted-foreground">
        Pull tous les abonnements, paiements, factures et virements de Stripe
        vers le CRM. À lancer après une 1ère config ou si la DB s'est
        désynchronisée. Idempotent : peut être relancé sans risque.
      </p>
      <div className="flex items-center gap-2">
        <label className="text-[11px] text-muted-foreground flex items-center gap-1">
          Sur les
          <input
            type="number"
            min={1}
            max={3650}
            value={sinceDays}
            onChange={(e) =>
              setSinceDays(Math.max(1, Number(e.target.value) || 365))
            }
            className="w-16 rounded border bg-background px-1 py-0.5 text-xs"
            disabled={running}
          />
          derniers jours
        </label>
        <Button size="sm" variant="secondary" onClick={run} disabled={running}>
          {running ? "Backfill en cours…" : "Lancer le backfill"}
        </Button>
      </div>
      {(progress.length > 0 || counts || error) && (
        <pre
          className={cn(
            "text-[11px] p-2 rounded-md overflow-auto max-h-48",
            error
              ? "bg-destructive/10 text-destructive"
              : "bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100",
          )}
        >
          {error
            ? `Erreur : ${error}`
            : [
                ...progress,
                counts ? `\nFinal : ${JSON.stringify(counts, null, 2)}` : null,
              ]
                .filter(Boolean)
                .join("\n")}
        </pre>
      )}
    </div>
  );
}

function BillionMailTestBlock() {
  const notify = useNotify();
  const [email, setEmail] = useState("");
  return (
    <EdgeFnTestBlock
      title="Tester avec un contact"
      description="Pousse un faux contact vers la liste de prospection pour valider base_url + admin_key + list_id + endpoint."
      fn="billionmail_push_contact"
      body={() => {
        if (!email.includes("@")) {
          notify("Email invalide", { type: "error" });
          return null;
        }
        return { email, name: "BillionMail Test" };
      }}
      runningLabel="Envoi…"
      controls={
        <Input
          type="email"
          placeholder="test+bm@fabrik.so"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-8 text-xs"
        />
      }
    />
  );
}

/** Bouton → invoke d'une edge function → panneau ok / erreur (JSON brut). */
function EdgeFnTestBlock({
  title,
  description,
  fn,
  body,
  runningLabel,
  summarize,
  controls,
}: {
  title: string;
  description: string;
  fn: string;
  /** null = ne pas lancer (validation côté appelant). */
  body: () => Record<string, unknown> | null;
  runningLabel: string;
  summarize?: (resp: Record<string, unknown>) => string | undefined;
  controls?: React.ReactNode;
}) {
  const notify = useNotify();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<null | {
    ok: boolean;
    body: string;
    summary?: string;
  }>(null);

  const run = async () => {
    const payload = body();
    if (!payload) return;
    setRunning(true);
    setResult(null);
    const { data, error } = await getSupabaseClient().functions.invoke(fn, {
      body: payload,
    });
    if (error) {
      setResult({ ok: false, body: error.message });
      notify(`Erreur ${fn}: ${error.message}`, { type: "error" });
    } else {
      const resp = data as { ok?: boolean } & Record<string, unknown>;
      setResult({
        ok: !!resp.ok,
        body: JSON.stringify(resp, null, 2),
        summary: summarize?.(resp),
      });
    }
    setRunning(false);
  };

  return (
    <div className="rounded-md border border-border bg-muted/20 p-3 flex flex-col gap-2">
      <p className="text-xs font-medium">{title}</p>
      <p className="text-[11px] text-muted-foreground">{description}</p>
      <div className="flex items-center gap-2">
        {controls}
        <Button size="sm" variant="secondary" onClick={run} disabled={running}>
          {running ? runningLabel : "Tester"}
        </Button>
        {result?.summary && (
          <span
            className={cn(
              "text-xs",
              result.ok
                ? "text-emerald-700 dark:text-emerald-300"
                : "text-destructive",
            )}
          >
            {result.summary}
          </span>
        )}
      </div>
      {result && (
        <pre
          className={cn(
            "text-[11px] p-2 rounded-md overflow-auto max-h-40",
            result.ok
              ? "bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
              : "bg-destructive/10 text-destructive",
          )}
        >
          {result.body}
        </pre>
      )}
    </div>
  );
}

function emptyValues(
  def: IntegrationDef,
  row: IntegrationRow | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of def.fields) {
    if (field.secret) {
      // Secret fields always start empty in the UI; placeholder tells user
      // whether a value is already stored.
      out[field.key] = "";
      continue;
    }
    const v = row?.config?.[field.key];
    out[field.key] = typeof v === "string" ? v : "";
  }
  return out;
}

function isConfigured(
  def: IntegrationDef,
  row: IntegrationRow | undefined,
): boolean {
  if (!row) return false;
  for (const field of def.fields) {
    const v = row.config?.[field.key];
    if (!v || (typeof v === "string" && !v.trim())) return false;
  }
  return true;
}
